<template>
  <main>
    <div class="container py-5">
      <div class="dashboard-container">
        <!-- Stats Row -->
        <div class="row g-4 mb-4">
          <div
              v-for="stat in stats"
              :key="stat.id"
              class="col-lg col-md-4 col-sm-6"
          >
            <div class="stat-card" :style="{ '--card-color': stat.color }" :class="{ 'form-loading': loading }">
              <div class="stat-icon" :style="{ background: stat.gradient }">
                <i :class="stat.icon"></i>
              </div>
              <div class="stat-label">{{ stat.label }}</div>
              <div class="stat-value">{{ stat.value }}</div>
              <div :class="['stat-trend', stat.trendDirection]">
                <i :class="`fas fa-arrow-${stat.trendDirection === 'up' ? 'up' : 'down'}`"></i>
                {{ stat.trend }}
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Row -->
        <div class="row">
          <!-- Signups Chart -->
          <div class="col-lg-6 mb-4">
            <div class="chart-card" :class="{ 'form-loading': loading }">
              <h3 class="chart-title">{{ lms_website.__('Signups in {0}', [current_year]) }}</h3>
              <VueApexCharts
                  v-if="!loading"
                  height="250"
                  :options="signupsChartOptions"
                  :series="signupsChartSeries"
              />
            </div>
          </div>

          <!-- Enrollments Chart -->
          <div class="col-lg-6 mb-4">
            <div class="chart-card" :class="{ 'form-loading': loading }">
              <h3 class="chart-title">{{ lms_website.__('Enrollments in {0}', [current_year]) }}</h3>
              <VueApexCharts
                  v-if="!loading"
                  height="250"
                  :options="enrollmentsChartOptions"
                  :series="enrollmentsChartSeries"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import {authData, isLoggedIn} from "../../utils/auth";

const numberToK = (num) => {
  if (num === null || num === undefined) return "0";
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\\.0$/, '') + "B";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\\.0$/, '') + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\\.0$/, '') + "K";
  return num.toString();
}


export default {
  name: "AboutUs",
  components: {
    VueApexCharts,
  },
  setup() {
    return {
      isLoggedIn,
      numberToK,
      moment,
      lms_website
    }
  },
  data() {
    return {
      authData: authData(),
      loading: true,
      current_year: new Date().getFullYear(),
      stats: [
        {
          id: 'courses',
          loading: true,
          label: lms_website.__('Courses'),
          value: '',
          trend: '12%',
          trendDirection: 'up',
          icon: 'fas fa-book-open',
          color: '#667eea',
          gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        },
        {
          id: 'signups',
          loading: true,
          label: lms_website.__('Signups'),
          value: '',
          trend: '8.3%',
          trendDirection: 'up',
          icon: 'fas fa-user-plus',
          color: '#f093fb',
          gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
        },
        {
          id: 'enrollments',
          loading: true,
          label: lms_website.__('Enrollments'),
          value: '',
          trend: '15.2%',
          trendDirection: 'up',
          icon: 'fas fa-clipboard-check',
          color: '#4facfe',
          gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
        },
        {
          id: 'completions',
          loading: true,
          label: lms_website.__('Completions'),
          value: '',
          trend: '5.7%',
          trendDirection: 'up',
          icon: 'fas fa-graduation-cap',
          color: '#43e97b',
          gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
        },
        {
          id: 'certifications',
          loading: true,
          label: lms_website.__('Certifications'),
          value: '',
          trend: '3.2%',
          trendDirection: 'up',
          icon: 'fas fa-certificate',
          color: '#fa709a',
          gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
        }
      ],
      // Signups Chart
      signupsChartSeries: [{
        name: 'Signups',
        data: []
      }],
      signupsChartOptions: {
        chart: {
          type: 'line',
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        colors: ['#0d6efd'],
        stroke: {
          curve: 'smooth',
          width: 2
        },
        markers: {
          size: 4,
          colors: ['#0d6efd'],
          strokeWidth: 0,
          hover: {
            size: 6
          }
        },
        grid: {
          borderColor: '#e7e7e7',
          strokeDashArray: 5,
          xaxis: {
            lines: {
              show: false
            }
          }
        },
        xaxis: {
          categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
          labels: {
            style: {
              fontSize: '11px'
            }
          }
        },
        yaxis: {
          tickAmount: 6,
          labels: {
            style: {
              fontSize: '11px'
            },
          }
        },
        tooltip: {
          theme: 'light',
          y: {
            formatter: function (val) {
              return numberToK(val)
            },
            title: {
              formatter: (seriesName) => seriesName + ":"
            }
          }
        }
      },
      // Enrollments Chart
      enrollmentsChartSeries: [{
        name: 'Enrollments',
        data: []
      }],
      enrollmentsChartOptions: {
        chart: {
          type: 'bar',
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        colors: [
          "#3b82f6", // Jan - blue
          "#06b6d4", // Feb - cyan
          "#10b981", // Mar - green
          "#84cc16", // Apr - lime
          "#facc15", // May - yellow
          "#f97316", // Jun - orange
          "#ef4444", // Jul - red
          "#ec4899", // Aug - pink
          "#a855f7", // Sep - purple
          "#6366f1", // Oct - indigo
          "#0ea5e9", // Nov - sky blue
          "#14b8a6"  // Dec - teal
        ],
        dataLabels: {
          enabled: false,
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '75%',
            borderRadius: 4,
            distributed: true,
            borderRadiusApplication: 'end'
          },
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        xaxis: {
          categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
          labels: {
            style: {
              colors: [
                "#3b82f6", // Jan - blue
                "#06b6d4", // Feb - cyan
                "#10b981", // Mar - green
                "#84cc16", // Apr - lime
                "#facc15", // May - yellow
                "#f97316", // Jun - orange
                "#ef4444", // Jul - red
                "#ec4899", // Aug - pink
                "#a855f7", // Sep - purple
                "#6366f1", // Oct - indigo
                "#0ea5e9", // Nov - sky blue
                "#14b8a6"  // Dec - teal
              ],
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          theme: 'light',
          y: {
            formatter: function (val) {
              return numberToK(val)
            },
            title: {
              formatter: (seriesName) => seriesName + ":"
            }
          }
        }
      },
    }
  },
  computed: {
    auth() {
      if (this.authData && this.authData.is_logged_in && this.authData.user) {
        return this.authData;
      }
    }
  },
  watch: {},
  methods: {
    initCharts() {
      this.loading = true;
      frappe.call("lms_website.api.about.get_stats").then(({message}) => {
        console.log('get_stats', message);
        if (message.stats && message.stats.length) {
          message.stats.map((item) => {
            const get_stats = this.stats.find((stat) => stat.id === item.id);
            if (get_stats) {
              get_stats.value = this.numberToK(item.value);
            }
          });
        }
        console.log('message.charts.Signups', message.charts.Signups)
        console.log('message.charts.Enrollments', message.charts.Enrollments)
        this.signupsChartSeries = [{
          name: 'Signups',
          data: message.charts.Signups
        }];
        this.enrollmentsChartSeries = [{
          name: 'Enrollments',
          data: message.charts.Enrollments
        }]
        this.loading = false;
      });
    },
  },
  mounted() {
    setTimeout(() => {
      NProgress.done();
      $("[data-background]").each(function () {
        $(this).css("background-image", "url( " + $(this).attr("data-background") + "  )");
      });
      this.initCharts();
    }, 500);
  },
  beforeUnmount() {
    // Destroy charts on component unmount
  },
}
</script>

<style lang="scss" scoped>
.dashboard-container {
  min-height: 500px;
}

.stat-card {
  background: white;
  border: 1px solid #dddddd;
  border-top-color: var(--card-color);
  border-top-width: 4px;
  border-radius: 16px;
  padding: 1.5rem;
  height: 100%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

//.stat-card::before {
//  content: '';
//  position: absolute;
//  top: 0;
//  left: 0;
//  right: 0;
//  height: 4px;
//  background: var(--card-color);
//}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 1rem;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stat-label {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  color: #2d3748;
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0;
  line-height: 1;
}

.stat-trend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.5rem;
  padding: 4px 8px;
  border-radius: 6px;
  background: #d4edda;
  color: #155724;
}

.stat-trend.down {
  background: #f8d7da;
  color: #721c24;
}

.chart-card {
  background: white;
  border: 1px solid #dddddd;
  border-radius: 16px;
  padding: 2rem;
  min-height: 320px;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  overflow: hidden;
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
}

.chart-subtitle {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.chart-container {
  position: relative;
  height: 250px;
}

.donut-chart-container {
  position: relative;
  height: 300px;
}
</style>
